import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import SendIcon from "@mui/icons-material/Send";
import SEO from "../SEO";
import { useTranslation } from "react-i18next";
import useServiceGuideEntry from "./useServiceGuideEntry";

const sectionHeadingSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  mb: 2.5,
};

const sectionTitleSx = {
  fontWeight: 800,
  fontSize: "1.25rem",
  color: "#2B2320",
  textAlign: "center",
  flex: 1,
};

const DesktopServiceGuideDetailsItem = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { service, isLoading } = useServiceGuideEntry();

  if (isLoading) {
    return (
      <Box sx={{ mt: 15, textAlign: "center" }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  if (!service) {
    return null; // The hook has already redirected to the guide.
  }

  const currencyName = service?.currency?.[0]?.name || "";
  const {
    name,
    applicationFee,
    compilationTime,
    isPdfExist,
    requiredAttachments = [],
    pdfPath,
    isForCitizen,
    workflowSteps = [],
  } = service;

  const infoCards = [
    {
      title: t("serviceGuideDetails_fee"),
      value: `${applicationFee ?? "-"} ${currencyName}`.trim(),
      icon: <ReceiptLongOutlinedIcon sx={{ color: "#2E7D32" }} />,
      tint: "#FFFFFF",
      border: "rgba(15,23,42,0.08)",
      valueColor: "#2E7D32",
    },
    {
      title: t("serviceGuideDetails_duration"),
      value: compilationTime ? t("serviceGuideDetails_durationDays", { count: compilationTime, days: compilationTime }) : t("serviceGuideDetails_notSpecified"),
      icon: <ScheduleRoundedIcon sx={{ color: "#0F6CBD" }} />,
      tint: "#FFFFFF",
      border: "rgba(15,23,42,0.08)",
      valueColor: "#0F6CBD",
    },
    {
      title: t("serviceGuideDetails_guideFile"),
      value: isPdfExist ? t("serviceGuideDetails_fileAvailable") : t("serviceGuideDetails_fileUnavailable"),
      icon: <PictureAsPdfIcon sx={{ color: "#C62828" }} />,
      tint: "#FFFFFF",
      border: "rgba(15,23,42,0.08)",
      valueColor: "#C62828",
      action: true,
    },
  ];

  const openPdf = () => {
    window.open(pdfPath, "_blank");
  };

  const handleRequestService = () => {
    nav("/service-request", { state: service });
  };

  return (
    <>
      <SEO title={name} />
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            p: { xs: 2.5, md: 4 },
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Stack gap={3.5}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={3}>
                <Stack gap={1.25} sx={{ maxWidth: 680 }}>
                  <Typography
                    sx={{
                      width: "fit-content",
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 99,
                      backgroundColor: "action.hover",
                      color: "text.secondary",
                      fontWeight: 800,
                      fontSize: "0.9rem",
                    }}
                  >
                    {t("serviceGuideDetails_badge")}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: "1.8rem", md: "2.35rem" },
                      fontWeight: 800,
                      color: "#2B2320",
                      lineHeight: 1.25,
                    }}
                  >
                    {name}
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "1rem" }}>
                    {t("serviceGuideDetails_intro")}
                  </Typography>
                </Stack>

                <Stack direction="row" gap={1.25} alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={openPdf}
                    disabled={!isPdfExist}
                    startIcon={<DescriptionOutlinedIcon />}
                    sx={{
                      px: 2.5,
                      py: 1.2,
                      borderRadius: 99,
                      backgroundColor: "background.paper",
                      fontWeight: 800,
                    }}
                  >
                    {t("serviceGuideDetails_openGuide")}
                  </Button>
                  {isForCitizen && (
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleRequestService}
                      sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: 99,
                        fontWeight: 800,
                        boxShadow: "0 4px 14px 0 rgba(0,118,255,0.39)",
                      }}
                    >
                      {t("nav_submitRequest", "تقديم الطلب")}
                    </Button>
                  )}
                </Stack>
              </Stack>

                <Alert
                  severity={isForCitizen ? "success" : "info"}
                  sx={{
                    mt: 2,
                    border: "1px solid",
                    borderColor: isForCitizen ? "success.light" : "divider",
                    alignItems: "center",
                    "& .MuiAlert-message": { fontWeight: 700 },
                  }}
                >
                  {isForCitizen
                    ? t("serviceGuideDetails_onlineAlert")
                    : t("serviceGuideDetails_municipalityAlert")}
                </Alert>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 2,
              }}
            >
              {infoCards.map((card) => (
                <Paper
                  key={card.title}
                  elevation={0}
                  sx={{
                    p: 2.25,
                    border: `1px solid ${card.border}`,
                    background: card.tint,
                    boxShadow: "none",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                    <Stack gap={0.6} sx={{ textAlign: "center", flex: 1 }}>
                      <Typography sx={{ color: "#7A6A5E", fontWeight: 700, fontSize: "0.92rem" }}>
                        {card.title}
                      </Typography>
                      <Typography sx={{ color: card.valueColor, fontWeight: 800, fontSize: "1.1rem" }}>
                        {card.value}
                      </Typography>
                    </Stack>
                    {card.action ? (
                      <IconButton
                        onClick={openPdf}
                        disabled={!isPdfExist}
                        sx={{
                          width: 52,
                          height: 52,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "background.paper",
                        }}
                      >
                        {card.icon}
                      </IconButton>
                    ) : (
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: "18px",
                          display: "grid",
                          placeItems: "center",
                          backgroundColor: "background.paper",
                          border: `1px solid ${card.border}`,
                        }}
                      >
                        {card.icon}
                      </Box>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", backgroundColor: "background.paper" }}>
              <Box sx={sectionHeadingSx}>
                <Box sx={{ width: 64, height: 2, backgroundColor: "#D8C7B9" }} />
                <Typography sx={sectionTitleSx}>{t("serviceGuideDetails_requiredAttachments")}</Typography>
                <Box sx={{ width: 64, height: 2, backgroundColor: "#D8C7B9" }} />
              </Box>

              {requiredAttachments.length > 0 ? (
                <Box sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #E6DBD0" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr",
                      background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      color: "#fff",
                    }}
                  >
                    <Typography sx={{ py: 1.6, px: 2, textAlign: "center", fontWeight: 800 }}>{t("serviceGuideDetails_attachmentType")}</Typography>
                    <Typography sx={{ py: 1.6, px: 2, textAlign: "center", fontWeight: 800 }}>{t("serviceGuideDetails_mandatory")}</Typography>
                    <Typography sx={{ py: 1.6, px: 2, textAlign: "center", fontWeight: 800 }}>{t("serviceGuideDetails_optional")}</Typography>
                  </Box>

                  {requiredAttachments.map((attachment, index) => (
                    <Box
                      key={`${attachment.attachmentType}-${index}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr",
                        alignItems: "center",
                        backgroundColor: index % 2 === 0 ? "#FFFDFC" : "#FBF7F2",
                        borderTop: index === 0 ? "none" : "1px solid #EFE4D9",
                      }}
                    >
                      <Typography sx={{ py: 1.8, px: 2, textAlign: "center", fontWeight: 700, color: "#2B2320" }}>
                        {attachment.attachmentType}
                      </Typography>
                      <Box sx={{ display: "grid", placeItems: "center" }}>
                        {attachment.isMandatory ? <CheckCircleRoundedIcon sx={{ color: "#2E7D32" }} /> : <Typography sx={{ color: "#C5B7AB" }}>-</Typography>}
                      </Box>
                      <Box sx={{ display: "grid", placeItems: "center" }}>
                        {!attachment.isMandatory ? <CheckCircleRoundedIcon sx={{ color: "#0F6CBD" }} /> : <Typography sx={{ color: "#C5B7AB" }}>-</Typography>}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper elevation={0} sx={{ p: 3, textAlign: "center", backgroundColor: "#FBF7F2", border: "1px dashed #D8C7B9" }}>
                  <Typography sx={{ fontWeight: 700, color: "#75685F" }}>
                    {t("serviceGuideDetails_noAttachments")}
                  </Typography>
                </Paper>
              )}
            </Paper>

            {workflowSteps.length > 0 && (
              <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", backgroundColor: "background.paper" }}>
                <Box sx={sectionHeadingSx}>
                  <Box sx={{ width: 64, height: 2, backgroundColor: "#D8C7B9" }} />
                  <Typography sx={sectionTitleSx}>{t("serviceGuideDetails_workflow")}</Typography>
                  <Box sx={{ width: 64, height: 2, backgroundColor: "#D8C7B9" }} />
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    px: 0.25,
                    pb: 1,
                    "&::-webkit-scrollbar": {
                      height: 8,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.18)",
                      borderRadius: 99,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      width: "max-content",
                      minWidth: "100%",
                      justifyContent: workflowSteps.length <= 8 ? "center" : "flex-start",
                      py: 0.5,
                    }}
                  >
                    {workflowSteps.map((step, index) => (
                      <React.Fragment key={`${step}-${index}`}>
                        <Paper
                          elevation={0}
                          sx={{
                            width: 188,
                            minHeight: 126,
                            p: 1.75,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "#fff",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 54,
                              height: 54,
                              mx: "auto",
                              mb: 1.1,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              backgroundColor: "primary.main",
                              color: "#fff",
                              fontWeight: 800,
                              fontSize: "1.08rem",
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography sx={{ fontWeight: 800, color: "#2D2622", lineHeight: 1.6, fontSize: "0.98rem" }}>
                            {step}
                          </Typography>
                        </Paper>

                        {index < workflowSteps.length - 1 && (
                          <Box
                            sx={{
                              minWidth: 22,
                              height: 126,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "text.disabled",
                              flexShrink: 0,
                            }}
                          >
                            <ArrowBackRoundedIcon sx={{ fontSize: 22 }} />
                          </Box>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Paper>
            )}
          </Stack>
        </Paper>
      </Box>
    </>
  );
};

export default DesktopServiceGuideDetailsItem;
